/** @jsx jsx */
import { CloudCheckFillIcon, Flex, Loader, Paragraph, Tooltip, WarningFillIcon } from "@eworkit/iris";
import { useBreakpointIndex } from "@theme-ui/match-media";
import { FC } from "react";
import { useTranslation } from "react-i18next";
import { jsx } from "theme-ui";

import { agoDate } from "@/core/dates";

import { useApplyWizard } from "./ApplyWizardContext";

interface ApplyModalSavingIndicatorProps {}

export const ApplyModalSavingIndicator: FC<ApplyModalSavingIndicatorProps> = ({}) => {
  const { t } = useTranslation("session");

  const breakpoint = useBreakpointIndex();

  const { draft, savingState, savedDate } = useApplyWizard();

  if (draft?.status === "SUBMITTED") {
    return null;
  }

  if (savingState === "SAVING") {
    return (
      <Flex alignItems="center" gap="xs">
        {breakpoint > 2 && (
          <Paragraph size="p-md" color="n700">
            {t("JOB_REQUESTS.APPLY.WIZARD.SAVING")}
          </Paragraph>
        )}

        <Loader size="md" color="vg500" />
      </Flex>
    );
  }

  if (savingState === "SAVED_RECENTLY") {
    return (
      <Flex alignItems="center" gap="xs">
        {breakpoint > 2 && (
          <Paragraph size="p-md" color="n700">
            {t("JOB_REQUESTS.APPLY.WIZARD.SAVED_AS_DRAFT")}
          </Paragraph>
        )}

        <CloudCheckFillIcon size="md" color="g500" />
      </Flex>
    );
  }

  if (savingState === "SAVED") {
    return (
      <Flex>
        {breakpoint > 2 ? (
          <Tooltip
            content={t("JOB_REQUESTS.APPLY.WIZARD.SAVED_AGO", { ago: agoDate(savedDate?.toISOString()) })}
            placement="top">
            {tooltipProps => <CloudCheckFillIcon size="md" color="n500" {...tooltipProps} />}
          </Tooltip>
        ) : (
          <CloudCheckFillIcon size="md" color="n500" />
        )}
      </Flex>
    );
  }

  if (savingState === "ERROR") {
    return (
      <Flex alignItems="center" gap="xs">
        {breakpoint > 2 && (
          <Paragraph size="p-md" color="n700">
            {t("JOB_REQUESTS.APPLY.WIZARD.SAVING_ERROR")}
          </Paragraph>
        )}

        {breakpoint > 2 ? (
          <Tooltip content={t("JOB_REQUESTS.APPLY.WIZARD.SAVING_ERROR_TOOLTIP")} placement="top">
            {tooltipProps => <WarningFillIcon size="md" color="r500" {...tooltipProps} />}
          </Tooltip>
        ) : (
          <WarningFillIcon size="md" color="r500" />
        )}
      </Flex>
    );
  }

  return null;
};
